import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { useState } from 'react';
import propTypes from '../../../node_modules/prop-types/index.js';
import { uniqueId, safeRest, noop } from '../../utils/functions.js';
import handle from '../../utils/handle.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { ButtonWrapper, IconWrapper, ButtonLabel } from './styledComponents.js';
import useDerivedState from '../../hooks/useDerivedState.js';
import useTheme from '../../hooks/useTheme.js';

var _excluded = ["id", "ariaLabel", "onClick", "children", "icon", "hoverIcon", "customStyle"];

var ActionButton = function ActionButton(_ref) {
  var controlledId = _ref.id,
      ariaLabel = _ref.ariaLabel,
      onClick = _ref.onClick,
      children = _ref.children,
      icon = _ref.icon,
      hoverIcon = _ref.hoverIcon,
      customStyle = _ref.customStyle,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useDerivedState = useDerivedState(uniqueId('actionbtn-id-'), controlledId),
      _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
      id = _useDerivedState2[0];

  var theme = useTheme();
  var safeProps = safeRest(rest);

  var _safeCustomStyle = safeCustomStyle(customStyle),
      buttonStyle = _safeCustomStyle.buttonStyle,
      iconStyle = _safeCustomStyle.iconStyle;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isHovered = _useState2[0],
      setIsHovered = _useState2[1];

  var ButtonIcon = isHovered ? hoverIcon : icon;

  if (!ButtonIcon) {
    ButtonIcon = icon;
  }

  return /*#__PURE__*/React.createElement(ButtonWrapper, _extends({}, safeProps, {
    id: id,
    "aria-label": ariaLabel,
    onClick: handle(onClick),
    onMouseOver: function onMouseOver() {
      return setIsHovered(true);
    },
    onFocus: function onFocus() {
      return setIsHovered(true);
    },
    onMouseOut: function onMouseOut() {
      return setIsHovered(false);
    },
    onBlur: function onBlur() {
      return setIsHovered(false);
    },
    buttonStyle: buttonStyle,
    theme: theme
  }), ButtonIcon && /*#__PURE__*/React.createElement(IconWrapper, {
    iconStyle: iconStyle,
    theme: theme
  }, ButtonIcon), /*#__PURE__*/React.createElement(ButtonLabel, null, children));
};

process.env.NODE_ENV !== "production" ? ActionButton.propTypes = {
  /** ID is required for Automated Testing, Google analytics and Google Tag Manager */
  id: propTypes.string,

  /** Aria-label for button */
  ariaLabel: propTypes.string,

  /** Icon to adorn button */
  icon: propTypes.node,

  /** Icon to adorn button with when hovered or focused */
  hoverIcon: propTypes.node,

  /** Click handler for button */
  onClick: propTypes.func,

  /** Content of the button */
  children: propTypes.node.isRequired,

  /** Style overrides */
  customStyle: propTypes.shape({
    buttonStyle: propTypes.object,
    iconStyle: propTypes.object
  })
} : void 0;
ActionButton.defaultProps = {
  id: undefined,
  ariaLabel: undefined,
  icon: undefined,
  hoverIcon: undefined,
  onClick: noop,
  customStyle: {
    buttonStyle: {},
    iconStyle: {}
  }
};
process.env.NODE_ENV !== "production" ? ActionButton.displayName = 'ActionButton' : void 0;

export default ActionButton;
